import { Badge, Button } from "@light-technology/components";
import { ChevronRight } from "@untitledui/icons";
import { Link } from "react-router";
import { type AppResponse, type PlanResponse } from "../../state/api.types";
import { planDetailsLink, plansLink } from "../../utilities/links";

type LinkedPlansProps = {
  app: AppResponse;
  linkedPlanUUIDs: string[];
  plans: PlanResponse[];
};

const LinkedPlanCard = ({
  app,
  plan,
}: {
  app: AppResponse;
  plan: PlanResponse | undefined;
}) => {
  if (!plan) return null;

  const statusColor = plan.state === "active" ? "success" : "gray";
  const statusLabel =
    plan.state === "active"
      ? "Live"
      : plan.state === "draft"
        ? "Draft"
        : "Archived";

  return (
    <Link
      to={planDetailsLink(app.slug, plan.uuid)}
      className="flex items-center rounded-xl border border-secondary py-xl pr-xs pl-xl transition-colors hover:border-primary"
    >
      <div className="flex flex-1 items-center gap-2">
        <span className="font-semibold text-secondary">{plan.name}</span>
        <Badge size="sm" color={statusColor} type="pill-color">
          {statusLabel}
        </Badge>
      </div>
      <Button size="sm" color="tertiary" iconLeading={ChevronRight} />
    </Link>
  );
};

export const LinkedPlans = ({
  app,
  linkedPlanUUIDs,
  plans,
}: LinkedPlansProps) => {
  const planMap = new Map<string, PlanResponse>();

  for (const plan of plans) {
    planMap.set(plan.uuid, plan);
  }
  if (linkedPlanUUIDs.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3xl text-center">
        <div className="flex max-w-[352px] flex-col gap-1">
          <div className="font-semibold">No plans linked</div>
          <div className="text-sm text-tertiary">
            This program can always be attached to a plan by the API. Edit a
            plan to link the program by default.
          </div>
        </div>
        <Button color="secondary" href={plansLink(app.slug)}>
          View plans
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="flex flex-col gap-3xl">
        {linkedPlanUUIDs.map((uuid) => (
          <LinkedPlanCard key={uuid} app={app} plan={planMap.get(uuid)} />
        ))}
        {/* TODO add these back when editing is enabled
      <div className="flex flex-col gap-3xl pt-3xl text-sm text-tertiary">
        <p>
          <span className="font-semibold">
            Do I have to offer this program with the above plans?
          </span>
          <br />
          No, these plans just include the program by default. You can omit the
          program via the API.
        </p>
        <p>
          <span className="font-semibold">
            How do I add this program to another plan?
          </span>
          <br />
          Programs can be added when editing a plan.
        </p>
      </div> */}
      </div>
    </div>
  );
};
